import { cn } from '@/shared/utils/formatting';
import type { ReactNode } from 'react';

type PageShellVariant = 'feed' | 'watch' | 'chat';
type PageShellSpacing = 'none' | 'sm' | 'md' | 'lg';

const SPACING_CLASS: Record<PageShellSpacing, string> = {
  none: '',
  sm: 'py-4',
  md: 'py-6',
  lg: 'py-12',
};

interface PageShellProps {
  variant?: PageShellVariant;
  spacing?: PageShellSpacing;
  padded?: boolean;
  sidebar?: ReactNode;
  className?: string;
  children: ReactNode;
}

/**
 * The single page-level wrapper. Every `page.tsx` and `loading.tsx` inside
 * `src/app/(platform)/**` and `src/app/studio/**` wraps content in <PageShell>.
 *
 * - variant="feed": full-viewport width capped at max-w-site, flat 16px gutter
 * - variant="watch": capped primary column at 1800px with optional sidebar slot
 * - variant="chat": full-height centered reading column; the chat surface owns
 *   its own internal scroll + pinned composer, so spacing is left to the child
 *
 * `w-full` is mandatory — mx-auto inside a flex-column parent (SidebarInset)
 * would otherwise collapse to intrinsic content width (.claude/rules/styling-layout.md).
 */
export function PageShell({
  variant = 'feed',
  spacing = 'md',
  padded = true,
  sidebar,
  className,
  children,
}: PageShellProps) {
  if (variant === 'watch') {
    const hasSidebar = Boolean(sidebar);
    // Watch caps by column geometry, not by max-w-site. Sidebar absent → primary cap (1800).
    // Sidebar present → grid total: primary (1800) + gap (gap-4 = 16) + secondary (400) = 2216.
    // If gap-4 is ever changed, bump this literal alongside it.
    const watchOuter = cn(
      'mx-auto w-full',
      hasSidebar ? 'max-w-[2216px]' : 'max-w-[1800px]',
      padded && 'page-px',
      SPACING_CLASS[spacing],
      className
    );

    if (!hasSidebar) {
      return (
        <div className={watchOuter}>
          <div className="min-w-0">{children}</div>
        </div>
      );
    }

    return (
      <div className={watchOuter}>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1800px)_400px]">
          <div className="min-w-0">{children}</div>
          <aside className="min-w-0">{sidebar}</aside>
        </div>
      </div>
    );
  }

  if (variant === 'chat') {
    // Viewport-bound frame; the chat surface owns its centered column, internal
    // scroll, and pinned composer. No max-width / gutter here. h-full would
    // resolve against the shell's unbounded min-h-dvh column (content height),
    // so the height is pinned to viewport minus header + impersonation banner —
    // same formula as the shorts feed (shorts-feed-container.tsx).
    return (
      <div
        className={cn(
          'flex h-[calc(100dvh-var(--impersonate-offset,0px)-var(--header-height,4rem))] min-h-0 w-full flex-col',
          className
        )}
      >
        {children}
      </div>
    );
  }

  const feedOuter = cn(
    'mx-auto w-full max-w-site',
    padded && 'page-px',
    SPACING_CLASS[spacing],
    className
  );
  return <div className={feedOuter}>{children}</div>;
}
